import { useCallback, useEffect, useState } from 'react'
import type { AtelierDTO, PieceForgeableDTO, Stat } from '@whatremains/shared'
import { EffetObjet, NomObjet } from './NomObjet'
import { api, ErreurApi } from '../api'
import { empiler } from '@whatremains/shared'
import { IconeObjet } from './IconeObjet'
import { TypeObjet } from './TypeObjet'

/**
 * L'atelier : améliorer, détruire, se soigner.
 *
 * Les trois gestes qui ferment la boucle de l'or. Il vit dans l'écran de négoce
 * parce qu'il répond à la même question que l'échoppe et le marché, « qu'est-ce
 * que je fais de ça », avec une troisième réponse : ni vendre ni garder tel
 * quel, mais transformer.
 *
 * Ce que l'écran doit rendre évident sans qu'on ait à calculer : **améliorer
 * coûte de l'or et de la matière, et la matière ne vient que de ce qu'on
 * détruit.** L'un ne va pas sans l'autre, et les deux sont côte à côte.
 */

const NOMS_STATS: Record<Stat, string> = {
  force: 'For', adresse: 'Adr', esprit: 'Esp', vigueur: 'Vig', fortune: 'Ftn',
}

function bonusEnLigne(bonus: Partial<Record<Stat, number>>): string {
  return Object.entries(bonus)
    .filter(([, v]) => v)
    .map(([s, v]) => `${NOMS_STATS[s as Stat]} +${v}`)
    .join(' · ')
}

/** Ce que le cran suivant changerait, dit en une ligne et pas en tableau. */
function Ecart({ p }: { p: PieceForgeableDTO }) {
  if (!p.apres) return <span className="tenu">Au bout de ce qu’on peut en faire.</span>

  const gagne: string[] = []
  for (const [s, v] of Object.entries(p.apres.bonus)) {
    const avant = p.bonus[s as Stat] ?? 0
    if ((v ?? 0) > avant) gagne.push(`${NOMS_STATS[s as Stat]} ${avant} → ${v}`)
  }
  if (p.apres.degats.max > p.degatsMax) {
    gagne.push(`dégâts ${p.degatsMin}-${p.degatsMax} → ${p.apres.degats.min}-${p.apres.degats.max}`)
  }
  return <span className="gain">{gagne.join(' · ')}</span>
}

export function Atelier({ id, surChangement }: { id: string; surChangement: () => void }) {
  const [a, setA] = useState<AtelierDTO | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const [voir, setVoir] = useState<'portees' | 'rangees'>('portees')

  const recharger = useCallback(async () => setA(await api.atelier(id)), [id])
  useEffect(() => { void recharger() }, [recharger])

  async function agir(action: () => Promise<unknown>, dire: (r: unknown) => string) {
    setErreur(null)
    try {
      setNote(dire(await action()))
      await recharger()
      surChangement()
    } catch (e) {
      setNote(null)
      setErreur(e instanceof ErreurApi ? e.message : "Quelque chose n'a pas abouti.")
    }
  }

  if (!a) return <section><p className="tenu">…</p></section>

  if (!a.ouvert) {
    return (
      <section>
        <p className="attenue">On ne te forge rien ici.</p>
      </section>
    )
  }

  /*
   * Les pièces identiques de la forge s'empilent, comme partout ailleurs.
   *
   * Enzo : « dans l'atelier, les éléments qui devraient être stackés ne le sont
   * pas. » Ses deux « Targe de relais » au même cran occupaient deux lignes.
   * Les matières étaient déjà comptées, l'équipement non.
   *
   * Le cran d'amélioration entre dans la clé, donc forger l'une des deux les
   * sépare aussitôt en deux lots — ce qui est exactement juste : à partir de
   * là, ce ne sont plus les mêmes pièces. La règle vient de `@whatremains/shared`,
   * la même qui range les sacs et le marché.
   *
   * Une pièce PORTÉE reste seule sur sa ligne : `empiler` s'en charge, et c'est
   * voulu — on ne forge pas « deux fois » ce qu'on a sur soi.
   */
  //
  // On ENVELOPPE la pièce au lieu de l'étaler : `empiler` a besoin d'un
  // `emplacement` qui vaut `null` pour ce qui est rangé, et la pièce en porte
  // désormais un vrai — celui où elle SE PORTE, qui n'est pas la même chose.
  // Les étaler ensemble écrasait l'un par l'autre.
  const lots = empiler(
    a.pieces
      .filter((p) => (voir === 'portees' ? p.porte : !p.porte))
      .map((p) => ({
        id: p.objetId,
        code: p.code,
        amelioration: p.amelioration,
        emplacement: p.porte ? 'porte' : null,
        piece: p,
      })),
  )

  return (
    <>
      {erreur && <p className="erreur">{erreur}</p>}
      {note && <p className="note">{note}</p>}

      <section>
        <div className="surtitre">Ce que tu as en matière</div>
        {a.matieres.length === 0 ? (
          <p className="attenue" style={{ maxWidth: '62ch' }}>
            Rien. La matière ne s’achète pas : elle vient de ce que tu détruis, et c’est la seule
            façon d’en avoir.
          </p>
        ) : (
          <div className="matieres">
            {a.matieres.map((m) => (
              <span className="matiere" key={m.code}>
                <IconeObjet
                  objet={{
                    code: m.code, categorie: m.categorie, famille: m.famille,
                    palier: m.palier, materiau: m.materiau,
                  }}
                  taille={22}
                />
                <b>{m.nombre}</b> {m.nom}
              </span>
            ))}
          </div>
        )}
      </section>

      <section>
        <div className="surtitre">La forge</div>
        {/*
          Le compte sur chaque onglet.

          Enzo, à l'atelier : « j'ai un couteau de fer et un couteau à sacs, or
          je n'ai plus ces items, je les ai vendus jour 1. C'est très bizarre. »
          Il les portait. La bascule s'ouvre sur « Portées », donc la liste
          qu'il lisait était celle de son équipement — mais rien ne disait
          combien il y avait de chaque côté, et un onglet actif se remarque mal
          quand on cherche autre chose. Le chiffre le dit sans phrase.
        */}
        <div className="bascule" style={{ marginBottom: 18 }}>
          <button aria-pressed={voir === 'portees'} onClick={() => setVoir('portees')}>
            Portées <span className="tenu">{a.pieces.filter((p) => p.porte).length}</span>
          </button>
          <button aria-pressed={voir === 'rangees'} onClick={() => setVoir('rangees')}>
            Dans les sacs <span className="tenu">{a.pieces.filter((p) => !p.porte).length}</span>
          </button>
        </div>

        {lots.length === 0 ? (
          <p className="tenu">Rien ici.</p>
        ) : (
          <div className="offres">
            {lots.map((lot) => {
              const p = lot.tete.piece
              return (
              <div className="offre" key={lot.cle}>
                <span className="offre-glyphe">
                  <IconeObjet
                    objet={{
                      code: p.code, categorie: p.categorie, famille: p.famille,
                      palier: p.palier, materiau: p.materiau,
                    }}
                    taille={34}
                  />
                </span>

                <div className="offre-corps">
                  <NomObjet
                    o={p}
                    suffixe={
                      <>
                        {p.amelioration > 0 && <em className="crans"> +{p.amelioration}</em>}
                        {lot.quantite > 1 && <span className="pile">×{lot.quantite}</span>}
                        <TypeObjet o={p} className="objet-type objet-type-ligne" />
                        <EffetObjet effet={p.effetTexte} />
                      </>
                    }
                  />
                  <span className="offre-chiffres">
                    {bonusEnLigne(p.bonus)}
                    {p.degatsMax > 0 && ` · ${p.degatsMin} à ${p.degatsMax} de dégâts`}
                    {` · cran ${p.amelioration} sur ${p.ameliorationMax}`}
                  </span>
                  <span className="offre-chiffres"><Ecart p={p} /></span>
                </div>

                <div className="offre-prix">
                  {p.cout ? (
                    <>
                      <b className={p.cout.orSuffisant ? undefined : 'tenu'}>{p.cout.or} or</b>
                      <span
                        className={`offre-chiffres${p.cout.matiereSuffisante ? '' : ' avertissement'}`}
                      >
                        {p.cout.nombre} {p.cout.matiereNom}
                      </span>
                      {/*
                        Où la trouver, dit au moment exact où elle manque.

                        Enzo : « j'ai besoin d'un verre pilé, je sais pas où
                        aller le chercher. Je vais dans une ville, ensuite dans
                        les gens, ensuite dans une autre ville… c'est très chiant
                        parce que je sais pas où aller chercher, il n'y a aucun
                        fun là-dedans. » Il cherchait dans les villes une matière
                        qui se tire de ses propres pièces.
                      */}
                      {p.cout.ouTrouver && (
                        <span className="matiere-ou">{p.cout.ouTrouver}</span>
                      )}
                      <button
                        className="discret"
                        disabled={!p.cout.orSuffisant || !p.cout.matiereSuffisante}
                        onClick={() =>
                          void agir(
                            () => api.ameliorer(id, p.objetId),
                            (r) => `${p.nom} passe au cran ${(r as { cran: number }).cran}.`,
                          )
                        }
                      >
                        forger
                      </button>
                    </>
                  ) : (
                    <span className="tenu">au bout</span>
                  )}

                  {!p.porte && (
                    <button
                      className="discret"
                      onClick={() =>
                        void agir(
                          () => api.detruire(id, p.objetId),
                          (r) => {
                            const x = r as { nombre: number }
                            return `${p.nom} défaite. ${x.nombre} ${p.rendu.matiereNom}.`
                          },
                        )
                      }
                    >
                      défaire · {p.rendu.nombre} {p.rendu.matiereNom.toLowerCase()}
                    </button>
                  )}
                </div>
              </div>
              )
            })}
          </div>
        )}
      </section>

      <section>
        <div className="surtitre">De quoi tenir</div>
        {a.consommables.length === 0 ? (
          <p className="tenu">Tu n’as rien.</p>
        ) : (
          <div className="paires">
            {a.consommables.map((c) => (
              <div className="paire paire-rachat" key={c.objetId}>
                <span className="paire-glyphe">
                  <IconeObjet
                    objet={{
                      code: c.code, categorie: c.categorie, famille: c.famille,
                      palier: c.palier, materiau: c.materiau,
                    }}
                    taille={26}
                  />
                </span>
                <b>
                  {c.nom}
                  {c.quantite > 1 && <span className="tenu"> ×{c.quantite}</span>}
                </b>
                <span className="chiffre">
                  {c.soin} points de vie
                  {!c.utile && <span className="tenu"> · tu es au complet</span>}
                </span>
                <button
                  className="discret"
                  disabled={!c.utile}
                  onClick={() =>
                    void agir(
                      () => api.consommer(id, c.objetId),
                      (r) => `${(r as { rendu: number }).rendu} points de vie.`,
                    )
                  }
                >
                  prendre
                </button>
              </div>
            ))}
          </div>
        )}
      </section>
    </>
  )
}
